import React, { useState } from 'react';
import { format } from 'date-fns';
import { 
  BarChart3, 
  Users, 
  MapPin, 
  Building, 
  TrendingUp, 
  Clock,
  FileText,
  Lightbulb,
  Save,
  Check,
  AlertCircle
} from 'lucide-react';
import { SessionState } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { MetricCard } from './MetricCard';
import { ChartRenderer } from './ChartRenderer';
import { TableRenderer } from './TableRenderer';

import { classNames } from '../../utils/classNames';
import { useDemo } from '../../contexts/DemoContext';
import apiService from '../../services/api';

interface AnalysisReportProps {
  session: SessionState;
}

export const AnalysisReport: React.FC<AnalysisReportProps> = ({ session }) => {
  const { layoutSpec, componentsPayload, evidencePack, sessionTopic } = session;
  const { isDemoMode } = useDemo();
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState<string>('');

  if (!session.analysisComplete || !layoutSpec) {
    return null;
  }

  const handleSaveReport = async () => {
    setSaveStatus('saving');
    setSaveMessage('');

    try {
      // Prepare the complete report data
      const reportData = {
        sessionTopic,
        layoutSpec,
        componentsPayload,
        evidencePack,
        timestamp: new Date().toISOString(),
        congressSessions: session.congressSessions,
        messages: session.messages
      };

      // Extract topic and query from session
      const topic = sessionTopic || 'unknown';
      const lastMessage = session.messages[session.messages.length - 1];
      const query = lastMessage?.role === 'user' ? lastMessage.content : 'analysis';

      const response = await apiService.saveReport({
        topic,
        query,
        report_data: reportData
      });

      setSaveStatus('success');
      setSaveMessage(response.message);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 5000);

    } catch (error) {
      console.error('Failed to save report:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to save report. Please try again.');
      
      // Reset error message after 5 seconds
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 5000);
    }
  };

  // Icon mapping for section headings
  const iconMap: Record<string, React.ReactNode> = {
    summary: <FileText className="w-5 h-5 text-blue-400" />,
    implications: <Lightbulb className="w-5 h-5 text-yellow-400" />,
    sponsor: <Users className="w-5 h-5 text-purple-400" />,
    actor: <Users className="w-5 h-5 text-purple-400" />,
    geography: <MapPin className="w-5 h-5 text-green-400" />,
    committee: <Building className="w-5 h-5 text-indigo-400" />,
    trend: <TrendingUp className="w-5 h-5 text-emerald-400" />,
    duration: <Clock className="w-5 h-5 text-orange-400" />,
    analysis: <BarChart3 className="w-5 h-5 text-blue-400" />,
  };

  const getIconForTitle = (title: string): React.ReactNode => {
    const lowercaseTitle = title.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowercaseTitle.includes(key)) {
        return icon;
      }
    }
    return <FileText className="w-5 h-5 text-blue-400" />;
  };

  type Accent = 'blue' | 'yellow' | 'purple' | 'green' | 'indigo' | 'emerald' | 'orange' | 'cyan';
  const getAccentForTitle = (sectionTitle?: string): Accent => {
    const t = (sectionTitle || '').toLowerCase();
    if (t.includes('strategic')) return 'yellow';
    if (t.includes('summary')) return 'blue';
    if (t.includes('committee')) return 'indigo';
    if (t.includes('trend')) return 'emerald';
    if (t.includes('duration')) return 'orange';
    if (t.includes('geography') || t.includes('state')) return 'green';
    if (t.includes('sponsor') || t.includes('actor')) return 'purple';
    return 'cyan';
  };

  const renderMetrics = () => {
    // Get both topic metrics and session baseline for comparison
    const comprehensiveComponent = componentsPayload?.get_comprehensive_key_metrics;
    const keyMetricsComponent = componentsPayload?.get_key_metrics;
    const sessionBaselineComponent = componentsPayload?.get_session_baseline;
    const evidenceMetrics = evidencePack.metrics;
    
    // Topic metrics
    let topicTotalBills = 'N/A';
    let topicBillsPassed = 'N/A';
    let topicPassageRate = 'N/A';
    let topicAvgCosponsors = 'N/A';
    
    // Session baseline metrics
    let baselineTotalBills = 'N/A';
    let baselineBillsPassed = 'N/A';
    let baselinePassageRate = 'N/A';
    let baselineAvgCosponsors = 'N/A';
    
    // Extract topic metrics
    if (comprehensiveComponent?.table_rows) {
      const rows = comprehensiveComponent.table_rows;
      const totalBillsRow = rows.find((r: any) => r.metric_id === 'total_bills');
      const passageRateRow = rows.find((r: any) => r.metric_id === 'passage_rate');
      const billsEnactedRow = rows.find((r: any) => r.metric_id === 'bills_enacted');
      const avgCosponsorsRow = rows.find((r: any) => r.metric_id === 'avg_cosponsors');
      
      if (totalBillsRow) topicTotalBills = totalBillsRow.topic_value?.toLocaleString() || 'N/A';
      if (billsEnactedRow) topicBillsPassed = billsEnactedRow.topic_value?.toLocaleString() || 'N/A';
      if (passageRateRow) topicPassageRate = `${passageRateRow.topic_value?.toFixed(1) || 0}%`;
      if (avgCosponsorsRow) topicAvgCosponsors = avgCosponsorsRow.topic_value?.toFixed(1) || 'N/A';
    }
    else if (keyMetricsComponent?.table_rows) {
      const rows = keyMetricsComponent.table_rows;
      rows.forEach((row: any) => {
        if (row.Metric === 'Total Bills Found') topicTotalBills = row.Value;
        if (row.Metric === 'Bills That Became Law') topicBillsPassed = row.Value;
        if (row.Metric === 'Passage Rate') topicPassageRate = row.Value;
        if (row.Metric === 'Average Cosponsors') topicAvgCosponsors = row.Value;
      });
    }
    else if (evidenceMetrics) {
      topicTotalBills = evidenceMetrics.total_bills?.toLocaleString() || 'N/A';
      topicBillsPassed = evidenceMetrics.bills_passed?.toLocaleString() || 'N/A';
      topicPassageRate = evidenceMetrics.passage_rate || 'N/A';
      topicAvgCosponsors = typeof evidenceMetrics.avg_cosponsors === 'number' 
        ? evidenceMetrics.avg_cosponsors.toFixed(1) 
        : evidenceMetrics.avg_cosponsors || 'N/A';
    }

    // Extract session baseline metrics
    if (sessionBaselineComponent?.table_rows) {
      const rows = sessionBaselineComponent.table_rows;
      rows.forEach((row: any) => {
        if (row.Metric === 'Total Bills in Session') baselineTotalBills = row.Value;
        if (row.Metric === 'Bills That Became Law') baselineBillsPassed = row.Value;
        if (row.Metric === 'Overall Passage Rate') baselinePassageRate = row.Value;
        if (row.Metric === 'Average Cosponsors') baselineAvgCosponsors = row.Value;
      });
    }

    const hasBaseline = sessionBaselineComponent?.table_rows;

    return (
      <div className="mb-8">
        <h2 className="text-xl font-semibold theme-text-analysis-primary mb-6 flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <span>Key Metrics</span>
        </h2>
        
        {/* Topic Metrics */}
        <div className="mb-6">
          <h3 className="text-lg font-medium theme-text-analysis-primary mb-4 flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <span>{sessionTopic} Topic</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              label="Total Bills Analyzed"
              value={topicTotalBills}
              icon={<FileText className="w-5 h-5 text-blue-400" />}
            />
            <MetricCard
              label="Bills That Became Law"
              value={topicBillsPassed}
              icon={<TrendingUp className="w-5 h-5 text-emerald-400" />}
            />
            <MetricCard
              label="Passage Rate"
              value={topicPassageRate}
              icon={<BarChart3 className="w-5 h-5 text-cyan-400" />}
            />
            <MetricCard
              label="Average Cosponsors"
              value={topicAvgCosponsors}
              icon={<Users className="w-5 h-5 text-purple-400" />}
            />
          </div>
        </div>

        {/* Session Baseline for Context */}
        {hasBaseline && (
          <div className="mb-6">
            <h3 className="text-lg font-medium theme-text-analysis-primary mb-4 flex items-center space-x-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span>Session Baseline (All Bills)</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                label="Total Bills in Session"
                value={baselineTotalBills}
                icon={<FileText className="w-5 h-5 text-gray-400" />}
              />
              <MetricCard
                label="Bills That Became Law"
                value={baselineBillsPassed}
                icon={<TrendingUp className="w-5 h-5 text-gray-400" />}
              />
              <MetricCard
                label="Overall Passage Rate"
                value={baselinePassageRate}
                icon={<BarChart3 className="w-5 h-5 text-gray-400" />}
              />
              <MetricCard
                label="Average Cosponsors"
                value={baselineAvgCosponsors}
                icon={<Users className="w-5 h-5 text-gray-400" />}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContextualInsights = () => {
    // Get data from both topic metrics and session baseline
    const comprehensiveComponent = componentsPayload?.get_comprehensive_key_metrics;
    const keyMetricsComponent = componentsPayload?.get_key_metrics;
    const sessionBaselineComponent = componentsPayload?.get_session_baseline;
    
    const insights = [];
    
    // If we have both topic and baseline data, provide comparative insights
    if ((keyMetricsComponent || comprehensiveComponent) && sessionBaselineComponent) {
      // Extract passage rates for comparison
      let topicPassageRate = null;
      let baselinePassageRate = null;
      
      if (comprehensiveComponent?.table_rows) {
        const passageRateRow = comprehensiveComponent.table_rows.find((r: any) => r.metric_id === 'passage_rate');
        topicPassageRate = passageRateRow?.topic_value;
      } else if (keyMetricsComponent?.table_rows) {
        const passageRateRow = keyMetricsComponent.table_rows.find((r: any) => r.Metric === 'Passage Rate');
        topicPassageRate = parseFloat(passageRateRow?.Value?.replace('%', '') || '0');
      }
      
      if (sessionBaselineComponent?.table_rows) {
        const baselineRow = sessionBaselineComponent.table_rows.find((r: any) => r.Metric === 'Overall Passage Rate');
        baselinePassageRate = parseFloat(baselineRow?.Value?.replace('%', '') || '0');
      }
      
      if (topicPassageRate !== null && baselinePassageRate !== null) {
        const difference = topicPassageRate - baselinePassageRate;
        const comparison = difference > 0.1 ? 'higher than' : 
                          difference < -0.1 ? 'lower than' : 'comparable to';
        const diffText = Math.abs(difference) > 0.1 ? ` (${difference > 0 ? '+' : ''}${difference.toFixed(1)}% difference)` : '';
        
        insights.push(`${sessionTopic} passage rate (${topicPassageRate.toFixed(1)}%) is ${comparison} the overall session baseline (${baselinePassageRate.toFixed(1)}%)${diffText}`);
      }
    }
    
    // Add other insights from comprehensive data if available
    if (comprehensiveComponent?.table_rows) {
      const rows = comprehensiveComponent.table_rows;
      const houseShareRow = rows.find((r: any) => r.metric_id === 'house_share');
      const bipartisanRow = rows.find((r: any) => r.metric_id === 'bipartisan_index');
      const demSponsorRow = rows.find((r: any) => r.metric_id === 'dem_sponsor_share');
      
      if (houseShareRow) {
        const houseShare = houseShareRow.topic_value;
        insights.push(`${houseShare?.toFixed(0)}% of bills originated in the House`);
      }
      
      if (demSponsorRow) {
        const demShare = demSponsorRow.topic_value;
        insights.push(`${demShare?.toFixed(0)}% of bills were sponsored by Democrats`);
      }
      
      if (bipartisanRow) {
        const bipartisan = bipartisanRow.topic_value;
        insights.push(`Average bipartisan support index: ${bipartisan?.toFixed(1)}`);
      }
    }

    if (insights.length === 0) return null;

    return (
      <div className="mb-8">
        <Card accent="blue" className="theme-bg-analysis-card">
          <div className="p-6">
            <h3 className="text-lg font-semibold theme-text-analysis-primary mb-4 flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              <span>Key Insights</span>
            </h3>
            <ul className="space-y-3">
              {insights.map((insight, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0" />
                  <p className="theme-text-analysis-secondary leading-relaxed text-base">{insight}</p>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      </div>
    );
  };

  const renderBlock = (block: any, index: number) => {
    switch (block.type) {
      case 'section_heading':
        const title = block.title || '(Untitled Section)';
        const icon = getIconForTitle(title);
        
        return (
          <div key={index} className="mb-6">
            <h2 className="text-xl font-semibold theme-text-analysis-primary mb-4 flex items-center space-x-2">
              {icon}
              <span>{title}</span>
            </h2>
          </div>
        );

      case 'text':
        const textContent = block.body || '';
        
        // Check if this is an executive summary (contains bullet points)
        const isExecutiveSummary = textContent.includes('•') || textContent.includes('- ');
        
        if (isExecutiveSummary) {
          // Determine section accent from heading; default to blue for true Executive Summary
          // Scan backwards to find the nearest preceding section heading
          let sectionAccent: Accent = 'cyan';
          for (let i = index - 1; i >= 0; i--) {
            const blk = (layoutSpec?.blocks || [])[i];
            if (blk.type === 'section_heading') {
              sectionAccent = getAccentForTitle(blk.title);
              break;
            }
          }

          // Parse bullet points into proper list items
          const bulletPoints: string[] = textContent
            .split(/\n/)
            .filter((line: string) => line.trim().match(/^[-•]\s/))
            .map((line: string) => line.replace(/^[-•]\s*/, '').trim())
            .filter((item: string) => item.length > 0);
            
          return (
            <div key={index} className="mb-8">
              <Card accent={sectionAccent} className="theme-bg-analysis-card">
                <div className="p-6">
                  <ul className="space-y-4">
                    {bulletPoints.map((point: string, idx: number) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0" />
                        <p className="theme-text-analysis-secondary leading-relaxed text-base">{point}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </div>
          );
        } else {
          // Regular text content with improved formatting
          // Determine section accent
          let sectionAccent: Accent = 'cyan';
          for (let i = index - 1; i >= 0; i--) {
            const blk = (layoutSpec?.blocks || [])[i];
            if (blk.type === 'section_heading') {
              sectionAccent = getAccentForTitle(blk.title);
              break;
            }
          }

          const processedText: string = textContent
            .replace(/\*\*(.*?)\*\*/g, '<strong class="theme-text-analysis-primary font-semibold">$1</strong>')
            .replace(/\*(.*?)\*/g, '<em class="theme-text-analysis-muted">$1</em>')
            .replace(/`(.*?)`/g, '<code class="bg-slate-700 text-blue-300 px-2 py-1 rounded text-sm font-mono">$1</code>')
            .split('\n')
            .filter((line: string) => line.trim().length > 0)
            .map((paragraph: string) => `<p class="theme-text-analysis-secondary leading-relaxed text-base mb-4 last:mb-0">${paragraph.trim()}</p>`)
            .join('');
          
          return (
            <div key={index} className="mb-6">
              <Card accent={sectionAccent}>
                <div 
                  className="prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: processedText }}
                />
              </Card>
            </div>
          );
        }

      case 'chart_group':
        const columnsMeta = block.columns || [];
        const spans = columnsMeta.map((c: any) => c.span || 6);
        
        // Find the nearest preceding section heading to determine accent color
        const nearestSectionBlock = (layoutSpec?.blocks || [])
          .slice(0, index)
          .reverse()
          .find(b => b.type === 'section_heading');
        const sectionAccent = getAccentForTitle(nearestSectionBlock?.title);
        
        return (
          <div key={index} className="mb-8">
            <div className={classNames(
              'grid gap-6',
              spans.length === 1 ? 'grid-cols-1' : 
              spans.length === 2 ? 'grid-cols-1 lg:grid-cols-2' :
              'grid-cols-1 lg:grid-cols-3'
            )}>
              {columnsMeta.map((colMeta: any, colIndex: number) => {
                const ref = colMeta.component_ref || '';
                if (!ref.includes(':')) {
                  return (
                    <Card key={colIndex} accent={sectionAccent}>
                      <p className="text-red-400">Invalid component reference: {ref}</p>
                    </Card>
                  );
                }

                const [, componentId] = ref.split(':', 2);
                const componentData = componentsPayload[componentId];
                
                if (!componentData) {
                  return (
                    <Card key={colIndex} accent={sectionAccent}>
                      <p className="text-slate-400">Data not found for component: {componentId}</p>
                    </Card>
                  );
                }

                // Determine component type based on actual data content, not just result_type
                const hasChartData = componentData.chart_json && componentData.chart_json.trim() !== '';
                const hasTableData = componentData.table_rows && Array.isArray(componentData.table_rows) && componentData.table_rows.length > 0;
                const isKPIBlock = componentData.component_kind === 'kpi_block';
                const isSpecialChart = componentData.component_kind === 'special_chart';
                
                if (isKPIBlock && hasTableData) {
                  // Simple KPI block - just show as a table without benchmarking
                  return (
                    <TableRenderer
                      key={colIndex}
                      data={componentData}
                      title={colMeta.title}
                      accent={sectionAccent}
                    />
                  );
                } else if (hasChartData || isSpecialChart) {
                  return (
                    <ChartRenderer
                      key={colIndex}
                      data={componentData}
                      title={colMeta.title}
                      accent={sectionAccent}
                    />
                  );
                } else if (hasTableData) {
                  return (
                    <TableRenderer
                      key={colIndex}
                      data={componentData}
                      title={colMeta.title}
                      accent={sectionAccent}
                    />
                  );
                } else {
                  // For debugging: log what we're getting
                  console.log('Unhandled component:', componentId, componentData);
                  
                  return (
                    <Card key={colIndex} accent={sectionAccent}>
                      <div className="p-4">
                        <h3 className="text-lg font-medium text-white mb-2">{colMeta.title}</h3>
                        <p className="text-slate-400">
                          No chart or table data available
                          <br />
                          <small>result_type: {componentData.result_type || 'undefined'}</small>
                          <br />
                          <small>component_kind: {componentData.component_kind || 'undefined'}</small>
                        </p>
                      </div>
                    </Card>
                  );
                }
              })}
            </div>
          </div>
        );

      default:
        return (
          <div key={index} className="mb-4">
            <Card>
              <p className="text-yellow-400">Unknown block type: {block.type}</p>
            </Card>
          </div>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Enhanced Header */}
      <div className="mb-8">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-emerald-500/10 rounded-2xl" />
          <Card variant="glow" className="relative backdrop-blur-sm border-slate-700/50">
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl mb-3 border border-blue-400/20">
                <BarChart3 className="w-7 h-7 text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent mb-2">
                Analysis for: {sessionTopic}
              </h1>
              <div className="w-20 h-[3px] bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mb-3" />
              <p className="text-slate-300 text-base mb-4">
                Report generated on {format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}
              </p>
              
              {/* Save Report Button - Only show in normal mode */}
              {!isDemoMode && (
                <div className="flex flex-col items-center space-y-2">
                  <Button
                    onClick={handleSaveReport}
                    disabled={saveStatus === 'saving'}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                      saveStatus === 'success' 
                        ? 'bg-green-500/20 border-green-400/30 text-green-400 hover:bg-green-500/30'
                        : saveStatus === 'error'
                        ? 'bg-red-500/20 border-red-400/30 text-red-400 hover:bg-red-500/30'
                        : 'bg-blue-500/20 border-blue-400/30 text-blue-400 hover:bg-blue-500/30'
                    }`}
                  >
                    {saveStatus === 'saving' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : saveStatus === 'success' ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Saved!</span>
                      </>
                    ) : saveStatus === 'error' ? (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        <span>Retry</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Report</span>
                      </>
                    )}
                  </Button>
                  
                  {saveMessage && (
                    <p className={`text-xs max-w-sm ${
                      saveStatus === 'success' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {saveMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Key Metrics */}
      {renderMetrics()}

      {/* Report Content */}
      <div className="space-y-6">
        {layoutSpec.blocks.map((block, index) => renderBlock(block, index))}
      </div>
    </div>
  );
};